import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { API_URL, getHeaders, Spinner, Empty, Pagination, PAGE_SIZE, AGE_FILTERS, getAgeCategory } from './shared';
import logger from '../../utils/logger';

export function IdentityMapping() {
  const [mapping, setMapping] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  const [openCountries, setOpenCountries] = useState({});
  const [countryPages, setCountryPages] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/portfolios/user-mapping`, { headers: getHeaders() });
        if (!res.ok) throw new Error('Failed');
        setMapping((await res.json()).mapping || []);
      } catch (err) { logger.error(err); toast.error('Failed to load mapping'); }
      finally { setLoading(false); }
    })();
  }, []);

  const roles = useMemo(() => {
    const s = new Set(mapping.map(m => m.role || 'user'));
    return ['all', ...Array.from(s).sort()];
  }, [mapping]);

  const { grouped, countries, totalFiltered } = useMemo(() => {
    const filtered = mapping.filter(m => {
      if (search) {
        const q = search.toLowerCase();
        if (!m.anonymous_id?.toLowerCase().includes(q) && !m.name?.toLowerCase().includes(q) && !m.email?.toLowerCase().includes(q)) return false;
      }
      if (roleFilter !== 'all' && (m.role || 'user') !== roleFilter) return false;
      if (ageFilter !== 'all' && getAgeCategory(m.created_at) !== ageFilter) return false;
      return true;
    });
    const grouped = {};
    filtered.forEach(m => {
      const country = m.country || 'Unspecified';
      if (!grouped[country]) grouped[country] = [];
      grouped[country].push(m);
    });
    const countries = Object.keys(grouped).sort((a, b) => {
      if (a === 'Unspecified') return 1;
      if (b === 'Unspecified') return -1;
      return a.localeCompare(b);
    });
    return { grouped, countries, totalFiltered: filtered.length };
  }, [mapping, search, roleFilter, ageFilter]);

  const toggleCountry = (c) => setOpenCountries(prev => ({ ...prev, [c]: !prev[c] }));
  const getPage = (c) => countryPages[c] || 1;
  const setPage = (c, p) => setCountryPages(prev => ({ ...prev, [c]: p }));

  if (loading) return <Spinner text="Loading identity mapping..." />;

  return (
    <div className="space-y-4">
      <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-xl p-3 text-xs text-yellow-400">
        This reveals real identities behind anonymous IDs. Owner-only access. All views are logged.
      </div>
      <div className="space-y-3">
        <input data-testid="mapping-search" type="text" placeholder="Search name, email, or anonymous ID..."
          value={search} onChange={e => { setSearch(e.target.value); setCountryPages({}); }}
          className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:border-green-500 transition" />
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Role:</span>
            <select data-testid="mapping-role-filter" value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setCountryPages({}); }}
              className="text-xs bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white focus:border-green-500">
              {roles.map(r => <option key={r} value={r}>{r === 'all' ? 'All Roles' : r}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Account Age:</span>
            <div className="flex gap-1">
              {AGE_FILTERS.map(f => (
                <button key={f.key} data-testid={`age-filter-${f.key}`} onClick={() => { setAgeFilter(f.key); setCountryPages({}); }}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition ${ageFilter === f.key ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500">{totalFiltered} users across {countries.length} {countries.length === 1 ? 'country' : 'countries'}</p>
      </div>

      {countries.length === 0 ? <Empty text="No users match your filters" /> : (
        <div className="space-y-2">
          {countries.map(country => {
            const items = grouped[country];
            const isOpen = openCountries[country] !== false;
            const page = getPage(country);
            const totalPages = Math.ceil(items.length / PAGE_SIZE);
            const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
            return (
              <div key={country} className="border border-gray-700 rounded-xl overflow-hidden" data-testid={`mapping-country-${country}`}>
                <button data-testid={`mapping-country-toggle-${country}`} onClick={() => toggleCountry(country)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-750 transition text-left">
                  <div className="flex items-center gap-3">
                    <span className="text-white font-medium text-sm">{country}</span>
                    <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">{items.length} users</span>
                  </div>
                  <span className="text-gray-500 text-xs">{isOpen ? '\u25B2' : '\u25BC'}</span>
                </button>
                {isOpen && (
                  <div className="bg-gray-900/50">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-gray-700">
                          <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Anonymous ID</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Joined</th>
                        </tr></thead>
                        <tbody>{pageItems.map((m, idx) => (
                          <tr key={`${m.real_user_id}-${idx}`} className="border-b border-gray-800 hover:bg-gray-800/50 transition">
                            <td className="py-2.5 px-3 font-mono text-xs text-gray-300">{m.anonymous_id}</td>
                            <td className="py-2.5 px-3 text-white">{m.name}</td>
                            <td className="py-2.5 px-3 text-gray-400 text-xs">{m.email}</td>
                            <td className="py-2.5 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${
                              (() => {
                                if (m.role === 'owner') return 'bg-purple-600/20 text-purple-400';
                                if (m.role === 'admin') return 'bg-blue-600/20 text-blue-400';
                                return 'bg-gray-700 text-gray-400';
                              })()
                            }`}>{m.role}</span></td>
                            <td className="py-2.5 px-3 text-gray-500 text-xs">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                    <div className="px-3 pb-3">
                      <Pagination page={page} totalPages={totalPages} onPageChange={(p) => setPage(country, p)} label={`${items.length} users in ${country}`} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
